import React from 'react';
import { useSortable } from '@dnd-kit/react/sortable';
import PropTypes from 'prop-types';
import { closestCorners, directionBiased } from '@dnd-kit/collision';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MoreOptionsComponent from 'components/ItemCard/MoreOptionsComponent';
import * as Ui from './SortableItem.styles';

const SortableItem = ({
  id,
  index,
  item,
  getItemGridInfo,
  renderItem,
  onRemove,
  onEdit,
  canEdit = true,
  isDraggable = false,
  isVerticalList = false,
}) => {
  const { ref, handleRef, isDragging } = useSortable({
    id,
    index,
    disabled: !isDraggable,
    collisionDetector: isVerticalList ? closestCorners : directionBiased,
  });

  const itemComponent = renderItem(item);
  const { gridColumnSpan, gridRowSpan, aspectRatio } = getItemGridInfo(item);

  const handleRemove = (e) => {
    e.stopPropagation();
    onRemove(id);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(id, item);
  };

  const options = [
    ...(onEdit && canEdit ? [{ label: 'Edit', onClick: handleEdit }] : []),
    ...(onRemove ? [{ label: 'Delete', onClick: handleRemove }] : []),
  ];

  const handleOptionsPointerDown = (event) => {
    event.stopPropagation();
  };

  return (
    <Ui.Container
      ref={ref}
      $isDraggable={isDraggable}
      $gridColumnSpan={gridColumnSpan}
      $gridRowSpan={gridRowSpan}
      $aspectRatio={aspectRatio}
      $isDragging={isDragging}
      className="d-flex justify-content-center"
      data-item-id={id}
    >
      <Ui.DraggableItem>{itemComponent}</Ui.DraggableItem>
      {isDraggable && (
        <Ui.DragHandle ref={handleRef} type="button" aria-label="Reorder module">
          <FontAwesomeIcon icon={faGripVertical} />
        </Ui.DragHandle>
      )}
      {options.length > 0 && (
        <Ui.OptionsSlot
          onPointerDown={handleOptionsPointerDown}
          onMouseDown={handleOptionsPointerDown}
          onClick={(event) => event.stopPropagation()}
        >
          <MoreOptionsComponent options={options} />
        </Ui.OptionsSlot>
      )}
    </Ui.Container>
  );
};

SortableItem.propTypes = {
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  getItemGridInfo: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  onEdit: PropTypes.func,
  canEdit: PropTypes.bool,
  isDraggable: PropTypes.bool,
  isVerticalList: PropTypes.bool,
};

export default SortableItem;
